import React from "react";
import {
  Card,
  Switch,
  Dropdown,
  Button,
  Typography,
  Space,
  Tag,
  type MenuProps,
} from "antd";
import { SettingOutlined, CalendarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ModeloCampanha } from "../../../services/modeloCampanhasService";

const { Text, Paragraph, Title } = Typography;

interface CardModeloCampanhaProps {
  modeloCampanha: ModeloCampanha;
  onToggleAtivo: () => void;
  onEditar: () => void;
}

export function CardModeloCampanha({
  modeloCampanha,
  onToggleAtivo,
  onEditar,
}: CardModeloCampanhaProps): JSX.Element {
  const navigate = useNavigate();

  const items: MenuProps["items"] = [
    { key: "edit", label: "Editar" },
    { key: "view", label: "Campanhas" },
  ];

  const onMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "edit") onEditar();
    else if (key === "view")
      navigate(`/gerenciarCampanhas?modeloId=${modeloCampanha.id}`);
  };

  return (
    <Card
      bordered
      className="shadow-md rounded-2xl hover:shadow-lg transition-all relative"
      style={{
        minHeight: 290,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* 🔹 Engrenagem flutuante — canto superior direito */}
      <div
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 10,
        }}
      >
        <Dropdown menu={{ items, onClick: onMenuClick }} trigger={["click"]}>
          <Button
            type="text"
            icon={<SettingOutlined style={{ fontSize: 18, color: "#003644" }} />}
          />
        </Dropdown>
      </div>

      {/* 🔹 Cabeçalho centralizado */}
      <div className="text-center mt-4 mb-2">
        <Title
          level={5}
          style={{
            margin: 0,
            color: "#003644",
            fontWeight: 600,
            wordBreak: "break-word",
          }}
        >
          {modeloCampanha.nome}
        </Title>
      </div>

      {/* 🔹 Descrição */}
      {modeloCampanha.descricao && (
        <Paragraph
          type="secondary"
          className="text-center text-gray-600"
          ellipsis={{ rows: 2 }}
          style={{ fontSize: 13 }}
        >
          {modeloCampanha.descricao}
        </Paragraph>
      )}

      {/* 🔹 Próxima Execução */}
      <div className="text-center mb-2">
        <Text type="secondary" style={{ fontSize: 12 }}>
          <CalendarOutlined />{" "}
          <strong>Próxima execução: </strong>{" "}
          {modeloCampanha.proximaExecucao
            ? dayjs(modeloCampanha.proximaExecucao).format("DD/MM/YYYY HH:mm")
            : "—"}
        </Text>
      </div>

      {/* 🔹 Informações principais */}
      <Space
        direction="vertical"
        size="small"
        className="w-full text-sm"
        style={{ marginTop: 6 }}
      >
        <Text>
          <strong>Categoria:</strong>{" "}
          {modeloCampanha.CategoriaMensagem?.nome || "—"}
        </Text>
        <Text>
          <strong>Seletor:</strong>{" "}
          {modeloCampanha.SeletorDestinatarios?.nome || "—"}
        </Text>
        <Text>
          <strong>Prioridade:</strong> {modeloCampanha.prioridade ?? "—"}
        </Text>
        <Text>
          <strong>Frequência:</strong>{" "}
          {modeloCampanha.frequenciaDias
            ? `${modeloCampanha.frequenciaDias} dias`
            : "—"}
        </Text>
        <Text>
          <strong>Limite mensal:</strong> {modeloCampanha.limiteMensal ?? "—"}
        </Text>
      </Space>

      {/* 🔹 Rodapé */}
      <div className="flex justify-between items-center mt-4 pt-2 border-t">
        <Tag
          color={modeloCampanha.ativo ? "green" : "red"}
          style={{
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: "12px",
          }}
        >
          {modeloCampanha.ativo ? "Ativo" : "Inativo"}
        </Tag>
        <Switch checked={modeloCampanha.ativo} onChange={onToggleAtivo} />
      </div>
    </Card>
  );
}
